import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

export type SubscriptionFormData = {
  id?: string;
  nursery_id: string;
  nursery_name: string;
  plan: string;
  status: string;
  amount: string;
  billing_cycle: string;
  started_at: string;
  expires_at: string;
  is_trial: boolean;
  trial_ends_at: string;
  payment_method: string;
  notes: string;
  discount_percent: string;
  discount_type: string;
  pricing_model: string;
  price_per_child: string;
  min_charge: string;
};

interface NurserySubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: SubscriptionFormData | null;
  onFormChange: (form: SubscriptionFormData) => void;
  onSave: () => void;
  isPending: boolean;
}

const NurserySubscriptionDialog = ({ open, onOpenChange, form, onFormChange, onSave, isPending }: NurserySubscriptionDialogProps) => {
  if (!form) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            تعديل اشتراك: <Badge variant="outline">{form.nursery_name}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {/* Plan & Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>الخطة</Label>
              <Select value={form.plan} onValueChange={(v) => onFormChange({ ...form, plan: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">أساسي (إداري)</SelectItem>
                  <SelectItem value="standard">قياسي (إداري + تعليمي)</SelectItem>
                  <SelectItem value="premium">مميز (كامل)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>الحالة</Label>
              <Select value={form.status} onValueChange={(v) => onFormChange({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">نشط</SelectItem>
                  <SelectItem value="suspended">موقوف</SelectItem>
                  <SelectItem value="expired">منتهي</SelectItem>
                  <SelectItem value="cancelled">ملغي</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Amount & Billing */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>المبلغ (ج.م)</Label>
              <Input type="number" value={form.amount} onChange={(e) => onFormChange({ ...form, amount: e.target.value })} dir="ltr" />
            </div>
            <div>
              <Label>دورة الفوترة</Label>
              <Select value={form.billing_cycle} onValueChange={(v) => onFormChange({ ...form, billing_cycle: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">شهري</SelectItem>
                  <SelectItem value="yearly">سنوي</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>طريقة الدفع</Label>
              <Select value={form.payment_method || "none"} onValueChange={(v) => onFormChange({ ...form, payment_method: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">غير محدد</SelectItem>
                  <SelectItem value="cash">نقدي</SelectItem>
                  <SelectItem value="bank_transfer">تحويل بنكي</SelectItem>
                  <SelectItem value="card">بطاقة</SelectItem>
                  <SelectItem value="wallet">محفظة إلكترونية</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Pricing Model */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>نموذج التسعير</Label>
              <Select value={form.pricing_model || "fixed"} onValueChange={(v) => onFormChange({ ...form, pricing_model: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">ثابت (باقة)</SelectItem>
                  <SelectItem value="per_child">حسب عدد الأطفال</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.pricing_model === "per_child" && (
              <>
                <div>
                  <Label>سعر الطفل (ج.م)</Label>
                  <Input type="number" value={form.price_per_child} onChange={(e) => onFormChange({ ...form, price_per_child: e.target.value })} dir="ltr" placeholder="مثال: 200" />
                </div>
                <div>
                  <Label>الحد الأدنى (ج.م)</Label>
                  <Input type="number" value={form.min_charge} onChange={(e) => onFormChange({ ...form, min_charge: e.target.value })} dir="ltr" placeholder="اختياري" />
                </div>
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>تاريخ البداية</Label>
              <Input type="date" value={form.started_at} onChange={(e) => onFormChange({ ...form, started_at: e.target.value })} dir="ltr" />
            </div>
            <div>
              <Label>تاريخ الانتهاء</Label>
              <Input type="date" value={form.expires_at} onChange={(e) => onFormChange({ ...form, expires_at: e.target.value })} dir="ltr" />
            </div>
          </div>

          {/* Discount */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>نسبة الخصم %</Label>
              <Input type="number" value={form.discount_percent} onChange={(e) => onFormChange({ ...form, discount_percent: e.target.value })} dir="ltr" />
            </div>
            <div>
              <Label>نوع الخصم</Label>
              <Select value={form.discount_type || "none"} onValueChange={(v) => onFormChange({ ...form, discount_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">بدون</SelectItem>
                  <SelectItem value="percentage">نسبة مئوية</SelectItem>
                  <SelectItem value="promo">كود ترويجي</SelectItem>
                  <SelectItem value="group">مجموعة</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Trial */}
          <div className="grid grid-cols-2 gap-3 items-end">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_trial"
                checked={form.is_trial}
                onChange={(e) => onFormChange({ ...form, is_trial: e.target.checked })}
                className="h-4 w-4 rounded border-input"
              />
              <Label htmlFor="is_trial">فترة تجريبية</Label>
            </div>
            {form.is_trial && (
              <div>
                <Label>تنتهي في</Label>
                <Input type="date" value={form.trial_ends_at} onChange={(e) => onFormChange({ ...form, trial_ends_at: e.target.value })} dir="ltr" />
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label>ملاحظات</Label>
            <Textarea value={form.notes} onChange={(e) => onFormChange({ ...form, notes: e.target.value })} rows={2} placeholder="ملاحظات إضافية..." />
          </div>

          <Button onClick={onSave} disabled={isPending} className="w-full">
            {isPending ? "جاري الحفظ..." : "حفظ تعديلات الاشتراك"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NurserySubscriptionDialog;
